import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Pagination } from "@hiya/ui";
import { listConsultations } from "../../../api/consultations";
import { formatDateTime } from "../../../utils/formatDate";
import type { ConsultationListItem, ConsultationTab } from "../../../types/consultation";
import { LoadingState, ErrorState, EmptyState, DataTable } from "../../../components/TableStates";
import { EditIcon, DocumentIcon } from "../../../components/Icons";
import { ActionButton } from "../../../components/ActionButton";

const PAGE_SIZE = 10;

const TAB_LABELS: { key: ConsultationTab; label: string }[] = [
  { key: "upcoming", label: "Upcoming Consultations" },
  { key: "history", label: "History" },
];

const COLUMNS = ["Date of consultation", "Case ID", "Actions"] as const;

/**
 * Renders the Actions cell content for one consultation row.
 *
 * - History + canceled: non-clickable "Cancelled" badge (HH-300 AC-a).
 *   role="img" + aria-label keeps the announcement reliable across SR
 *   (ARIA 1.2 ignores aria-label on bare span / role=generic).
 * - History + notes_published: secondary "View Notes" button.
 * - Otherwise (upcoming, draft, completed, no_show): primary "Take notes".
 *
 * Extracted from the JSX to keep the table render flat and to satisfy
 * SonarCloud's "no nested ternary" rule.
 */
function renderRowAction(
  tab: ConsultationTab,
  item: ConsultationListItem,
  navigate: ReturnType<typeof useNavigate>,
) {
  if (tab === "history" && item.status === "canceled") {
    return (
      <span
        role="img"
        aria-label="Consultation cancelled"
        className="inline-block px-2 py-0.5 rounded-lg text-xs font-medium bg-bg-tertiary text-text-secondary"
      >
        Cancelled
      </span>
    );
  }
  if (tab === "history" && item.status === "notes_published") {
    return (
      <ActionButton
        onClick={() => navigate(`/dashboard/consultations/${item.consultation_id}`)}
        icon={<DocumentIcon size={16} />}
        label="View Notes"
        variant="secondary"
      />
    );
  }
  return (
    <ActionButton
      onClick={() => navigate(`/dashboard/consultations/${item.consultation_id}`)}
      icon={<EditIcon />}
      label="Take notes"
    />
  );
}

export function ConsultationsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<ConsultationTab>("upcoming");
  const [items, setItems] = useState<ConsultationListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (t: ConsultationTab, p: number) => {
    try {
      setLoading(true);
      setError(null);
      const data = await listConsultations(t, p, PAGE_SIZE);
      setItems(data.items);
      setTotal(data.total);
    } catch {
      setError("Something went wrong. Please refresh the page.");
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(tab, page);
  }, [load, tab, page]);

  function handleTabChange(newTab: ConsultationTab) {
    setTab(newTab);
    setPage(1);
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div>
      {/* Segment control (pill tabs) */}
      <div className="bg-bg-secondary rounded-[32px] p-[2px] inline-flex mb-5">
        {TAB_LABELS.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => handleTabChange(key)}
            className={`px-5 py-2 text-base font-medium rounded-[30px] transition-colors ${
              tab === key
                ? "bg-bg-primary text-text-primary"
                : "text-text-primary"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {loading && <LoadingState columns={3} />}

      {!loading && error && <ErrorState message={error} />}

      {!loading && !error && items.length === 0 && <EmptyState message="No consultations found" />}

      {!loading && !error && items.length > 0 && (
        <>
          <DataTable columns={COLUMNS}>
            {items.map((item) => (
              <tr key={item.consultation_id} className="border-b border-stroke-primary last:border-b-0">
                <td className="px-4 py-3 text-sm text-text-primary">
                  {formatDateTime(item.scheduled_time)}
                </td>
                <td className="px-4 py-3 text-sm text-text-primary">
                  {item.case_id ?? "-"}
                </td>
                <td className="px-4 py-3 text-center">
                  {renderRowAction(tab, item, navigate)}
                </td>
              </tr>
            ))}
          </DataTable>
          <div className="mt-5">
            <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
          </div>
        </>
      )}
    </div>
  );
}
